<template>
  <div class="extracted-details">
    <BaseButton
      v-if="backTarget"
      stylevariant="primary"
      :cus_style="{ width: 'fit-content', padding: '0.4rem 0.875rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1rem' }"
      @click="goBackToSource"
    >
      <i class="las la-arrow-left"></i>
      {{ backTarget.label }}
    </BaseButton>

    <!-- VAT Statement Details -->
    <VATStatementDetails 
      v-if="fileType === 'vat_statement' && processedExtractedData"
      :data="processedExtractedData"
    />

    <!-- Trade License Details -->
    <TradeLicenseDetails 
      v-else-if="fileType === 'trade_license' && processedExtractedData"
      :data="processedExtractedData"
    />

    <!-- Emirates ID Details -->
    <EmiratesIDDetails 
      v-else-if="fileType === 'emirates_id' && processedExtractedData"
      :data="processedExtractedData"
    />

    <!-- Bank Statement Details -->
    <BankStatementDetails 
      v-else-if="fileType === 'bank_statement' && (processedExtractedData || dailyBalances)"
      :data="processedExtractedData || {}"
      :daily-balances="dailyBalances"
    />

    <!-- AECB Report Details -->
    <AECBReportDetails 
      v-else-if="fileType === 'aecb_report' && processedExtractedData"
      :data="processedExtractedData"
    />

    <!-- Audit Report Details -->
    <AuditReportDetails
      v-else-if="fileType === 'audit_report' && processedExtractedData"
      :data="processedExtractedData"
    />

    <!-- Payer List Details -->
    <PayerListDetails 
      v-else-if="fileType === 'payer_list' && processedExtractedData"
      :data="processedExtractedData"
    />

    <!-- Credit Model Details -->
    <CreditModelDetails 
      v-else-if="fileType === 'credit_model' && (processedExtractedData || customersRelationshipData)"
      :data="processedExtractedData || {}"
      :customers-relationship-data="customersRelationshipData"
    />

    <!-- ERP Extract Details -->
    <ErpExtractDetails
      v-else-if="fileType === 'erp_extract' && processedExtractedData"
      :data="processedExtractedData"
    />

    <!-- No Data Message -->
    <div v-else-if="!processedExtractedData" class="no-data">
      <div class="no-data-icon">
        <i class="las la-file-alt"></i>
      </div>
      <h3>No Extracted Data</h3>
      <p>No extracted details are available for this file type.</p>
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/UI/BaseButton.vue'
import {
  VATStatementDetails,
  TradeLicenseDetails,
  EmiratesIDDetails,
  BankStatementDetails,
  AECBReportDetails,
  PayerListDetails,
  CreditModelDetails,
  AuditReportDetails,
  ErpExtractDetails
} from './extracted-details'

const FILE_TYPE_BACK_TARGETS = {
  bank_statement: { name: 'chatbot-candidate-bank-statement', label: 'Bank Statements' },
  multi_account_statement: { name: 'chatbot-candidate-bank-statement', label: 'Bank Statements' },
  vat_statement: { name: 'chatbot-candidate-vat', label: 'VAT Statements' },
  emirates_id: { name: 'chatbot-candidate-kyb', label: 'KYB Documents' },
  trade_license: { name: 'chatbot-candidate-kyb', label: 'KYB Documents' },
  aecb_report: { name: 'chatbot-candidate-kyb', label: 'KYB Documents' },
  moa: { name: 'chatbot-candidate-kyb', label: 'KYB Documents' },
  aoa: { name: 'chatbot-candidate-kyb', label: 'KYB Documents' },
  audit_report: { name: 'chatbot-candidate-kyb', label: 'KYB Documents' },
  payer_list: { name: 'chatbot-automated-assessment-input', label: 'Automated Assessment' },
  credit_model: { name: 'chatbot-automated-assessment-input', label: 'Automated Assessment' },
  erp_extract: { name: 'chatbot-candidate-overview', label: 'Overview' }
}

export default {
  name: 'ExtractedDetails',
  components: {
    BaseButton,
    VATStatementDetails,
    TradeLicenseDetails,
    EmiratesIDDetails,
    BankStatementDetails,
    AECBReportDetails,
    PayerListDetails,
    CreditModelDetails,
    AuditReportDetails,
    ErpExtractDetails
  },
  props: {
    fileType: {
      type: String,
      required: true
    },
    extractedData: {
      type: Array,
      default: null
    },
    dailyBalances: {
      type: Object,
      default: null
    },
    customersRelationshipData: {
      type: Array,
      default: null
    }
  },
  computed: {
    processedExtractedData() {
      if (!this.extractedData) return null;
      
      // For most file types, the data is in the first element of the array
      if (Array.isArray(this.extractedData)) {
        return this.extractedData[0];
      }
      
      return this.extractedData;
    },
    candidateId() {
      return this.$route?.params?.candidateId || null;
    },
    backTarget() {
      if (!this.candidateId) return null;
      return FILE_TYPE_BACK_TARGETS[this.fileType] || null;
    }
  },
  mounted() {
    console.log('extractedData', this.extractedData);
    console.log('processedExtractedData', this.processedExtractedData);
  },
  methods: {
    goBackToSource() {
      if (!this.backTarget || !this.candidateId) return;

      this.$router.push({
        name: this.backTarget.name,
        params: { candidateId: this.candidateId }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.extracted-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  
  .no-data-icon {
    font-size: 3rem;
    color: #adb5bd;
    margin-bottom: 1rem;
  }
  
  h3 {
    color: #6c757d;
    margin: 0 0 0.5rem 0;
    font-size: 1.2rem;
    font-family: $brand-font-medium;
  }
  
  p {
    color: #adb5bd;
    margin: 0;
    font-family: $brand-font-regular;
  }
}
</style>
